import { useEffect, useState } from "react";
import { Icon } from "@/lib/icons";
import { useApp } from "@/hooks/use-app-context";
import { complaintsApi } from "@/api/complaints.api";
import type { ComplaintType } from "@/pages/complaints/complaints.types";

interface ComplaintRecord {
  id: string;
  type: ComplaintType;
  message: string;
  status: string;
  createdAt: string;
}

const typeLabelKey: Record<ComplaintType, string> = {
  bug: "complaintsBug",
  suggestion: "complaintsSuggestion",
  other: "complaintsOther",
};

export function ComplaintsHistory() {
  const { t } = useApp();
  const [items, setItems] = useState<ComplaintRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await complaintsApi.getMyComplaints();
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Icon name="loader2" className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-muted-foreground">
        <Icon name="messageSquare" className="h-10 w-10" />
        <p className="text-sm">{t.complaintsNoHistory}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div
          key={item.id}
          className="rounded-xl border border-border/40 bg-background/60 px-4 py-3"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="rounded-full border border-border/30 px-2.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide text-muted-foreground">
              {t[typeLabelKey[item.type]]}
            </span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted-foreground">
              {new Date(item.createdAt).toLocaleDateString()}
            </span>
          </div>
          <p className="mt-2 text-sm text-foreground/80 line-clamp-2">{item.message}</p>
        </div>
      ))}
    </div>
  );
}
